import React from "react";
import { css, cx, styled } from "../../theme/styled";
import { Button } from "../buttons/Button";
import { Card } from "../card/Card";
import { AlertFilledIcon } from "../icons/new-icons";
import { Body, Heading } from "../typography/Typography";
import { Flex } from "../util/Flex";
import { Margin } from "../util/Margin";

export type SeverityLevel = "low" | "high";

interface Props extends Omit<React.ComponentProps<"div">, "onClick"> {
  children: React.ReactNode;
  severity?: SeverityLevel;
}

const ErrorCardComponent = (props: Props): React.JSX.Element => {
  const ErrorCardStyles = css({
    display: "flex",
    flexDirection: "row",
    background: props.severity === "low"
      ? "$surface-neutral"
      : "$surface-error-light",
  });
  return (
    <div
      data-component-type="ErrorCard"
      className={cx(Card.Variant.Neutral, Card.Size.Small, ErrorCardStyles)}
      {...props}
    >
      <IconWrapper>
        <AlertFilledIcon
          colorToken={props.severity === "low"
            ? "$error-text"
            : "$on-surface-error-light"}
          size={24}
        />
      </IconWrapper>

      <Flex
        direction="column"
        alignItems="start"
        justifyContent="center"
        gap="$spacing-4"
      >
        {props.children}
      </Flex>
    </div>
  );
};

const ErrorTitle = (
  props: Required<React.PropsWithChildren>,
): React.JSX.Element => <Heading size="small" {...props} />;
const ErrorText = (
  props: Required<React.PropsWithChildren>,
): React.JSX.Element => <Body size="medium" {...props} />;
const ErrorButton = (
  props: React.ComponentProps<typeof Button>,
): React.JSX.Element => (
  <Margin top={12}>
    <Button {...props} />
  </Margin>
);

export const ErrorCard = Object.assign(ErrorCardComponent, {
  Title: ErrorTitle,
  Text: ErrorText,
  Button: ErrorButton,
});

const IconWrapper = styled.div({
  display: "flex",
  flexShrink: 0,
  marginRight: "$spacing-16",
});
